import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link, useLocation, useNavigate } from 'react-router';
import { z } from 'zod';

import { ApiError } from '@/api/client';
import { Alert } from '@/components/ui/Alert';
import { Button } from '@/components/ui/Button';
import { TextField } from '@/components/ui/TextField';
import { TurnstileWidget } from '@/components/ui/TurnstileWidget';
import { readSelectedPlan, subscribePathFor } from '@/features/billing/lib/planSelection';
import { useT } from '@/lib/i18n';
import { useCaptcha } from '@/lib/useCaptcha';

import type { SocialSession } from '../api/socialAuth';
import { SocialAuth } from '../components/social/SocialAuth';
import { useAuth } from '../hooks/useAuth';
import { clearPasswordChanged, passwordJustChanged } from '../lib/passwordChanged';

function loginSchema(t: ReturnType<typeof useT>) {
  return z.object({
    email: z.string().min(1, t('auth.common.email_required')).email(t('auth.common.email_invalid')),
    password: z.string().min(1, t('auth.login.password_required')),
  });
}

type FormValues = z.infer<ReturnType<typeof loginSchema>>;

export function LoginPage() {
  const { signIn, reload } = useAuth();
  const t = useT();
  const navigate = useNavigate();
  const location = useLocation();
  const [formError, setFormError] = useState<string | null>(null);
  const [unverifiedEmail, setUnverifiedEmail] = useState<string | null>(null);
  const captcha = useCaptcha();
  // Arriving from a password change: both change forms sign out and end here,
  // and a sign-in page identical to a cold visit says nothing happened.
  const [passwordChanged] = useState(passwordJustChanged);
  useEffect(() => {
    if (passwordChanged) clearPasswordChanged();
  }, [passwordChanged]);

  const schema = useMemo(() => loginSchema(t), [t]);
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({ resolver: zodResolver(schema) });

  const from = (location.state as { from?: string } | null)?.from;
  // A plan picked on the pricing page before signing up is still waiting: the
  // merchant goes straight to paying for it rather than to the dashboard. A
  // guard that sent them here from a specific page wins over it, and the
  // business guard on `/subscribe` sends them through onboarding first if they
  // never got that far.
  const selectedPlan = readSelectedPlan();
  const redirectTo = from ?? (selectedPlan ? subscribePathFor(selectedPlan) : '/app');

  const onSubmit = async (values: FormValues) => {
    setFormError(null);
    setUnverifiedEmail(null);
    if (captcha.pending) {
      setFormError(t('auth.login.captcha_pending'));
      return;
    }
    const token = captcha.token ?? undefined;
    try {
      await signIn(values.email, values.password, token);
      void navigate(redirectTo, { replace: true });
    } catch (error) {
      if (token) captcha.spend();
      captcha.absorb(error);
      if (error instanceof ApiError && error.code === 'EMAIL_NOT_VERIFIED') {
        setUnverifiedEmail(values.email);
        return;
      }
      setFormError(
        error instanceof ApiError ? error.message : t('auth.common.generic_error'),
      );
    }
  };

  // Google or WhatsApp can open an account nobody had yet, so a first sign-in
  // goes to setting up the business rather than to a dashboard with no shop.
  const onSocialSignIn = async (session: SocialSession) => {
    await reload();
    void navigate(session.is_new_account ? '/onboarding' : redirectTo, { replace: true });
  };

  return (
    <div>
      <h1 className="text-2xl font-bold tracking-tight text-ink">{t('auth.login.title')}</h1>
      <p className="mt-1.5 text-sm text-muted">{t('auth.login.subtitle')}</p>

      {passwordChanged ? (
        <Alert tone="success" className="mt-5">
          <span className="font-semibold">{t('auth.login.password_changed_title')}</span>{' '}
          {t('auth.login.password_changed')}
        </Alert>
      ) : null}

      <div className="mt-6">
        <SocialAuth mode="login" onAuthenticated={onSocialSignIn}>
          <form onSubmit={handleSubmit(onSubmit)} noValidate className="space-y-4">
            {formError ? <Alert tone="error">{formError}</Alert> : null}
            {unverifiedEmail ? (
              <Alert tone="info">
                {t('auth.login.unverified', { email: unverifiedEmail })}{' '}
                <Link to="/verify-email" className="font-medium text-brand-700 underline">
                  {t('auth.login.send_new_code')}
                </Link>
              </Alert>
            ) : null}

            <TextField
              label={t('auth.common.email')}
              type="email"
              autoComplete="email"
              placeholder="you@business.in"
              error={errors.email?.message}
              {...register('email')}
            />
            <TextField
              label={t('auth.common.password')}
              type="password"
              autoComplete="current-password"
              error={errors.password?.message}
              {...register('password')}
            />

            <div className="flex justify-end">
              <Link
                to="/forgot-password"
                className="text-sm font-medium text-brand-600 hover:underline"
              >
                {t('auth.login.forgot')}
              </Link>
            </div>

            {captcha.challenge ? (
              <TurnstileWidget
                key={captcha.widgetKey}
                challenge={captcha.challenge}
                onToken={captcha.setToken}
              />
            ) : null}

            <Button type="submit" size="lg" className="w-full" disabled={isSubmitting}>
              {isSubmitting ? t('auth.login.submitting') : t('auth.common.sign_in')}
            </Button>
          </form>
        </SocialAuth>
      </div>

      <p className="mt-6 text-center text-sm text-muted">
        {t('auth.login.new_here')}{' '}
        <Link to="/register" className="font-semibold text-brand-600 hover:underline">
          {t('auth.login.create_account')}
        </Link>
      </p>
    </div>
  );
}
